"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ImportTransaction = exports.ExportTransaction = exports.BaseTransaction = exports.TransactionFee = void 0;
const utils_1 = require("../utils");
const constants_1 = require("./constants");
const input_1 = require("./input");
const output_1 = require("./output");
const signature_1 = require("./signature");
const types_1 = require("./types");
class TransactionFee {
    chain;
    amount;
    assetId;
    constructor(chain, amount) {
        this.chain = chain;
        this.amount = amount;
        this.assetId = chain.assetId;
    }
}
exports.TransactionFee = TransactionFee;
class BaseTransaction {
    codecId;
    typeId;
    networkId;
    blockchainId;
    outputs;
    inputs;
    memo;
    constructor(typeId, networkId, blockchainId, outputs, inputs, memo) {
        this.codecId = constants_1.CodecId;
        this.typeId = typeId;
        this.networkId = networkId;
        this.blockchainId = blockchainId;
        this.outputs = outputs;
        this.outputs.sort(output_1.TransferableOutput.comparator);
        this.inputs = inputs;
        this.inputs.sort(input_1.TransferableInput.comparator);
        this.memo = memo;
    }
    getSignables() {
        return this.inputs;
    }
    getInputs() {
        return this.inputs;
    }
    getOutputs() {
        const outputs = [];
        for (const output of this.outputs) {
            outputs.push(output.output);
        }
        return outputs;
    }
    getOutput(index) {
        const outputs = this.getOutputs();
        if (index >= outputs.length) {
            throw new utils_1.TransactionError(`transaction does not have output at index ${index} > length (${outputs.length})`);
        }
        return outputs[index];
    }
    getUtxos() {
        const utxos = [];
        for (const transferable of this.inputs) {
            // should be Utxo here because transaction should be from builder
            // undefined should only be the case if it is an input from parsing bytes
            utxos.push(transferable.input.utxo);
        }
        return utxos;
    }
    async sign(signers) {
        const bytes = this.serialize();
        const signed = new Set();
        const signatures = [];
        for (const signable of this.getSignables()) {
            for (const signature of await signable.sign(bytes, signers)) {
                const key = signature.recoverAddress(bytes).serialize().toHex();
                if (signed.has(key)) {
                    continue;
                }
                signed.add(key);
                signatures.push(signature);
            }
        }
        return signatures;
    }
    async signTransaction(signers) {
        const bytes = this.serialize();
        const credentials = [];
        let credentialsSize = 0;
        for (const signable of this.getSignables()) {
            const signatures = await signable.sign(bytes, signers);
            if (signatures.length < signable.getThreshold()) {
                throw new utils_1.SignatureError('missing signer to complete signatures');
            }
            const creds = new signature_1.Secp256k1Credentials(signatures);
            const credentialBytes = creds.serialize();
            credentialsSize += credentialBytes.length;
            credentials.push(credentialBytes);
        }
        const buffer = utils_1.JuneoBuffer.alloc(bytes.length + 4 + credentialsSize);
        buffer.write(bytes);
        buffer.writeUInt32(credentials.length);
        for (const credential of credentials) {
            buffer.write(credential);
        }
        return buffer.toCHex();
    }
    serialize() {
        const outputsBytes = [];
        let outputsSize = 0;
        for (const output of this.outputs) {
            const bytes = output.serialize();
            outputsSize += bytes.length;
            outputsBytes.push(bytes);
        }
        const inputsBytes = [];
        let inputsSize = 0;
        for (const input of this.inputs) {
            const bytes = input.serialize();
            inputsSize += bytes.length;
            inputsBytes.push(bytes);
        }
        const buffer = utils_1.JuneoBuffer.alloc(
        // 2 + 4 + 4 + 4 + 4 + 4 = 22
        22 + constants_1.BlockchainIdSize + outputsSize + inputsSize + this.memo.length);
        buffer.writeUInt16(this.codecId);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt32(this.networkId);
        buffer.write(this.blockchainId);
        buffer.writeUInt32(this.outputs.length);
        for (const output of outputsBytes) {
            buffer.write(output);
        }
        buffer.writeUInt32(this.inputs.length);
        for (const input of inputsBytes) {
            buffer.write(input);
        }
        // since Durango upgrade memo cannot be used anymore
        // force it here to be empty to avoid issues with builders already used with memos
        buffer.writeUInt32(0); // 0 instead of this.memo.length
        buffer.writeString(''); // '' instead of this.memo
        return buffer;
    }
    static parse(data, expectedTypeId) {
        const buffer = utils_1.JuneoBuffer.from(data);
        const reader = buffer.createReader();
        // skip codec reading
        reader.skip(2);
        const typeId = reader.readAndVerifyTypeId(expectedTypeId);
        const networkId = reader.readUInt32();
        const blockchainId = new types_1.BlockchainId(reader.read(constants_1.BlockchainIdSize).toCB58());
        const outputsLength = reader.readUInt32();
        const outputs = [];
        for (let i = 0; i < outputsLength; i++) {
            const output = output_1.TransferableOutput.parse(reader.peekRemaining());
            reader.skip(output);
            outputs.push(output);
        }
        const inputsLength = reader.readUInt32();
        const inputs = [];
        for (let i = 0; i < inputsLength; i++) {
            const input = input_1.TransferableInput.parse(reader.peekRemaining());
            reader.skip(input);
            inputs.push(input);
        }
        const memoLength = reader.readUInt32();
        const memo = memoLength > 0 ? reader.readString(memoLength) : '';
        return new BaseTransaction(typeId, networkId, blockchainId, outputs, inputs, memo);
    }
}
exports.BaseTransaction = BaseTransaction;
class ExportTransaction extends BaseTransaction {
    destinationChain;
    exportedOutputs;
    constructor(typeId, networkId, blockchainId, outputs, inputs, memo, destinationChain, exportedOutputs) {
        super(typeId, networkId, blockchainId, outputs, inputs, memo);
        this.destinationChain = destinationChain;
        this.exportedOutputs = exportedOutputs;
        this.exportedOutputs.sort(output_1.TransferableOutput.comparator);
    }
    getOutputs() {
        const outputs = super.getOutputs();
        for (const output of this.exportedOutputs) {
            outputs.push(output.output);
        }
        return outputs;
    }
    serialize() {
        const baseTransaction = super.serialize();
        const exportedOutputsBytes = [];
        let exportedOutputsSize = 0;
        for (const output of this.exportedOutputs) {
            const bytes = output.serialize();
            exportedOutputsSize += bytes.length;
            exportedOutputsBytes.push(bytes);
        }
        const buffer = utils_1.JuneoBuffer.alloc(baseTransaction.length + constants_1.BlockchainIdSize + 4 + exportedOutputsSize);
        buffer.write(baseTransaction);
        buffer.write(this.destinationChain);
        buffer.writeUInt32(this.exportedOutputs.length);
        for (const output of exportedOutputsBytes) {
            buffer.write(output);
        }
        return buffer;
    }
    static parse(data, expectedTypeId) {
        const baseTx = BaseTransaction.parse(data, expectedTypeId);
        const buffer = utils_1.JuneoBuffer.from(data);
        const reader = buffer.createReader();
        reader.skip(baseTx);
        const destinationChain = new types_1.BlockchainId(reader.read(constants_1.BlockchainIdSize).toCB58());
        const exportedOutputsLength = reader.readUInt32();
        const exportedoutputs = [];
        for (let i = 0; i < exportedOutputsLength; i++) {
            const output = output_1.TransferableOutput.parse(reader.peekRemaining());
            reader.skip(output);
            exportedoutputs.push(output);
        }
        return new ExportTransaction(baseTx.typeId, baseTx.networkId, baseTx.blockchainId, baseTx.outputs, baseTx.inputs, baseTx.memo, destinationChain, exportedoutputs);
    }
}
exports.ExportTransaction = ExportTransaction;
class ImportTransaction extends BaseTransaction {
    sourceChain;
    importedInputs;
    constructor(typeId, networkId, blockchainId, outputs, inputs, memo, sourceChain, importedInputs) {
        super(typeId, networkId, blockchainId, outputs, inputs, memo);
        this.sourceChain = sourceChain;
        this.importedInputs = importedInputs;
        this.importedInputs.sort(input_1.TransferableInput.comparator);
    }
    getInputs() {
        return [...this.inputs, ...this.importedInputs];
    }
    getSignables() {
        return [...this.inputs, ...this.importedInputs];
    }
    getUtxos() {
        const utxos = super.getUtxos();
        for (const transferable of this.importedInputs) {
            // should be Utxo here because transaction should be from builder
            // undefined should only be the case if it is an input from parsing bytes
            utxos.push(transferable.input.utxo);
        }
        return utxos;
    }
    serialize() {
        const baseTransaction = super.serialize();
        const importedInputsBytes = [];
        let importedInputsSize = 0;
        for (const input of this.importedInputs) {
            const bytes = input.serialize();
            importedInputsSize += bytes.length;
            importedInputsBytes.push(bytes);
        }
        const buffer = utils_1.JuneoBuffer.alloc(baseTransaction.length + constants_1.BlockchainIdSize + 4 + importedInputsSize);
        buffer.write(baseTransaction);
        buffer.write(this.sourceChain);
        buffer.writeUInt32(this.importedInputs.length);
        for (const input of importedInputsBytes) {
            buffer.write(input);
        }
        return buffer;
    }
    static parse(data, expectedTypeId) {
        const baseTx = BaseTransaction.parse(data, expectedTypeId);
        const buffer = utils_1.JuneoBuffer.from(data);
        const reader = buffer.createReader();
        reader.skip(baseTx);
        const sourceChain = new types_1.BlockchainId(reader.read(constants_1.BlockchainIdSize).toCB58());
        const importedInputsLength = reader.readUInt32();
        const importedInputs = [];
        for (let i = 0; i < importedInputsLength; i++) {
            const input = input_1.TransferableInput.parse(reader.peekRemaining());
            reader.skip(input);
            importedInputs.push(input);
        }
        return new ImportTransaction(baseTx.typeId, baseTx.networkId, baseTx.blockchainId, baseTx.outputs, baseTx.inputs, baseTx.memo, sourceChain, importedInputs);
    }
}
exports.ImportTransaction = ImportTransaction;
